import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/membership", label: "Membership" },
  { to: "/find-a-region", label: "Find a Region" },
  { to: "/masterclass", label: "Masterclass" },
  { to: "/franchise", label: "Franchise" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-200/60 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-narrow flex items-center justify-between h-24">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_d32-network-in/artifacts/5qxy1am6_D32BN_logo_India.webp"
            alt="D32 Business Network India"
            className="h-12 sm:h-14 w-auto object-contain"
          />
          <span className="sr-only">D32 Business Network India</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-navy"
                    : "text-gray-700 hover:text-navy"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/visit-a-circle"
            data-testid="nav-cta-visit"
            className="inline-flex items-center bg-gold text-white font-semibold px-5 py-2.5 rounded-full hover:bg-[#5fc1f1] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_6px_20px_-6px_rgba(212,175,55,0.55)]"
          >
            Visit a Circle
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-navy p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-white border-t border-gray-200"
        >
          <div className="container-narrow py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-navy" : "text-gray-700"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/visit-a-circle"
              data-testid="nav-mobile-cta-visit"
              className="mt-2 inline-flex items-center justify-center bg-gold text-white font-semibold px-5 py-3 rounded-full"
            >
              Visit a Circle
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
